import React, { useState, useEffect } from "react";
import { DatePicker, Button, Input, message } from "antd";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { MinusIcon, PlusIcon } from "../../assets/icons";
import ProgressPhotos from "../../components/common/ProgressPhotos";
import ButtonLoader from "../../atoms/buttonLoader";
import { useLocation, useNavigate } from "react-router-dom";
import { getProjectById, sendProgressToClient, getWorkerProgress, getTasksData } from "../../services/service";
import { IMAGE_BASE_URL } from "../../config/api";
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectCurrentPlan } from '../../redux/selectors/authSelectors';
import { FEATURE_REQUIRED_PLANS } from '../../utils/planFeatureAccess';
import UpgradePromptModal from '../../components/common/UpgradePromptModal';

const SendProgress = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = location.state?.projectId;
  const progressId = location.state?.progressId;
  const taskId = location.state?.taskId;
  console.log("location", location.state);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(null);
  const [description, setDescription] = useState("");
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTaskSpecific, setIsTaskSpecific] = useState(false);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectCompletionPercentage, setProjectCompletionPercentage] = useState("");
  const [tasks, setTasks] = useState([]);
  const [progressType, setProgressType] = useState(progressId ? "task" : "project");
  const [projectTasks, setProjectTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const currentPlan = useSelector(selectCurrentPlan);
  const [workerInfo, setWorkerInfo] = useState(null);

  useEffect(() => {
    if (progressId) {
      fetchProgressPhotos();
    } else if (projectId) {
      fetchProjectPhotos();
    }
  }, [projectId, progressId]);

  useEffect(() => {
    if (projectId) {
      fetchProjectTasks();
    }
  }, [projectId]);

  const fetchProjectPhotos = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      const response = await getProjectById(projectId);
      if (response?.data) {
        const formattedPhotos = response?.data?.attachments?.map(attachment => ({
          id: attachment.id,
          url: `${IMAGE_BASE_URL}${attachment.url}`,
          commentCount: attachment.WPAttachmentComments?.length || 0
        })) || [];
        setProgressPhotos(formattedPhotos);
      }
    } catch (error) {
      console.error("Error fetching project photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressPhotos = async () => {
    try {
      setLoading(true);
      const response = await getWorkerProgress({ progress_id: progressId });
      if (response?.success) {
        const progress = response.data[0];
        const formattedPhotos = progress?.ProgressAttachments?.map(attachment => ({
          id: attachment.id,
          url: `${IMAGE_BASE_URL}${attachment.url}`,
        })) || [];
        setProgressPhotos(formattedPhotos);

        setTitle(progress.title || '');
        setDate(progress.date ? dayjs(progress.date) : null);
        setDescription(progress.description || '');

        // Set worker information
        if (progress?.User) {
          setWorkerInfo({
            id: progress.User.id,
            username: progress.User.username,
            image: progress.User.image
          });
        }
      }
    } catch (error) {
      console.error("Error fetching progress photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectTasks = async () => {
    try {
      setTasksLoading(true);
      const response = await getTasksData(1, 100, projectId, '', taskId);
      if (response?.success) {
        const formattedTasks = response.data.map(task => ({
          id: task.id,
          task: task.title,
          description: task.task_description,
          percentage: task.completeTaskPercentage?.toString() || "0",
          isSelected: taskId ? task.id === parseInt(taskId) : false
        }));
        setTasks(formattedTasks);

        if (taskId) {
          setIsTaskSpecific(true);
        }
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      message.error("Failed to fetch tasks");
    } finally {
      setTasksLoading(false);
    }
  };

  const handlePhotoSelect = (photo) => {
    setSelectedPhotos((prev) => {
      const already = prev.some((p) => p.id === photo.id);
      return already ? prev : [...prev, photo];
    });
  };

  const handleRemovePhoto = (photo) => {
    setSelectedPhotos((prev) => prev.filter((p) => p.id !== photo.id));
  };

  const handleTaskClick = () => {
    setIsTaskSpecific(!isTaskSpecific);
  };

  const canAccessFeature = () => {
    const planLevels = {
      'trial': 0,
      'basic': 1,
      'premium': 2,
      'ultimate': 3
    };
    return planLevels[currentPlan] >= planLevels[FEATURE_REQUIRED_PLANS.sendProgress];
  };

  const handleSubmit = async () => {
    if (!canAccessFeature()) {
      setShowUpgradeModal(true);
      return;
    }

    const trimmedTitle = (title || "").trim();
    const trimmedDescription = (description || "").trim();
    const isDateValid = dayjs.isDayjs(date) && date.isValid();
    const attachmentsSource = selectedPhotos.length > 0 ? selectedPhotos : progressPhotos;
    const hasAtLeastOneAttachment = Array.isArray(attachmentsSource) && attachmentsSource.length > 0;

    if (!trimmedTitle || !isDateValid || !trimmedDescription || !hasAtLeastOneAttachment) {
      message.error("Please fill all required fields and select at least one photo");
      return;
    }

    const selectedTasks = tasks.filter(task => task.isSelected);
    if (isTaskSpecific && selectedTasks.length === 0) {
      message.error("Please select at least one task");
      return;
    }

    const invalidTasks = selectedTasks.filter(task => {
      const percentage = parseInt(task.percentage);
      return isNaN(percentage) || percentage < 0 || percentage > 100;
    });

    if (invalidTasks.length > 0) {
      message.error("Task completion percentage must be between 0 and 100");
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        title: trimmedTitle,
        date: dayjs(date).format('YYYY-MM-DD'),
        project_id: projectId,
        description: trimmedDescription,
        attachments: attachmentsSource.map(photo => photo.id),
        type: progressType,
        stats: isTaskSpecific
          ? selectedTasks.map(task => ({
            id: task.id,
            completion_percentage: parseInt(task.percentage) || 0
          }))
          : [{
            id: projectId,
            completion_percentage: parseInt(projectCompletionPercentage) || 0
          }]
      };

      const response = await sendProgressToClient(payload);
      if (response.success) {
        message.success("Progress sent successfully");
        if (progressId) {
          navigate(`/progress-detail`, { state: { progressId } });
        } else {
          navigate("/project-details", { state: { projectId } });
        }
      }
    } catch (error) {
      console.error("Error sending progress:", error);
      message.error("Failed to send progress");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskCheckbox = (taskId) => {
    setTasks(tasks.map(task =>
      task.id === taskId ? { ...task, isSelected: !task.isSelected } : task
    ));
  };

  const handleTaskPercentage = (taskId, value) => {
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 100)) {
      setTasks(tasks.map(task =>
        task.id === taskId ? { ...task, percentage: value } : task
      ));
    }
  };

  return (
    <div className="w-full flex flex-col gap-6 pb-12">
      <ListHeader
        mainHeading="Project"
        placeholder="Search"
        btnShow={true}
        linkBreadCrum="/view-project"
        blinkBreadCrumText="Project"
        blinkBreadCrumText1="Send Progress to Client"
        searchShow={false}
        showLikeFilter={false}
        DropDown={false}
      />
      <div className="bg-white p-8 rounded-lg ">
        <h2 className="text-lg font-semibold mb-4">Progress</h2>

        {/* Worker Info Display */}
        {workerInfo && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg flex items-center gap-3">
            <img
              src={workerInfo.image ? `${IMAGE_BASE_URL}${workerInfo.image}` : "https://pnghq.com/wp-content/uploads/2023/02/employee-photo-placeholder-png-1000.png"}
              alt="Worker"
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <p className="text-sm text-gray-500">Progress by</p>
              <p className="font-medium text-gray-800">{workerInfo.username}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <Input
            placeholder="Title"
            className="h-[52px] focus:outline-none"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <DatePicker
            className="h-[52px] focus:outline-none w-full"
            value={date}
            onChange={setDate}
          />
          {isTaskSpecific === false ? (
            <>
              <Input
                placeholder="Project Completed"
                className="h-[52px] focus:outline-none"
                value={projectCompletionPercentage}
                onChange={(e) => setProjectCompletionPercentage(e.target.value)}
                type="number"
                min="0"
                max="100"
              />
            </>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {tasksLoading ? (
                <div className="text-center py-4">Loading tasks...</div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-4">No tasks found</div>
              ) : (
                tasks.map((item, index) => (
                  <div key={index} className="w-full grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        className="w-5 h-5 cursor-pointer border-gray-300 rounded-md accent-[#1E3A8A]"
                        id={`task-${index}`}
                        checked={item.isSelected}
                        onChange={() => handleTaskCheckbox(item.id)}
                      />
                      <label htmlFor={`task-${index}`} className="text-gray-700">
                        {item.task}
                      </label>
                    </div>
                    <div className="flex items-center justify-end w-full">
                      <Input
                        className="h-[40px] !hover:none w-20 focus:outline-none text-center"
                        value={item.percentage}
                        onChange={(e) => handleTaskPercentage(item.id, e.target.value)}
                        type="number"
                        min="0"
                        max="100"
                        suffix='%'
                        disabled={!item.isSelected}
                        style={{ textAlign: 'center' }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          <Input
            placeholder="Address"
            className="h-[52px] focus:outline-none"
          />
        </div>

        <div
          onClick={handleTaskClick}
          className="mb-4 flex items-center gap-1 cursor-pointer"
        >
          <span>{isTaskSpecific === false ? PlusIcon : MinusIcon}</span>
          <p className="font-medium text-[12px] text-[#1E3A8A]">
            {isTaskSpecific === true
              ? "Remove task-specific percentage"
              : "Add task-specific percentage"}
          </p>
        </div>

        <div className="mb-4">
          <Input.TextArea
            placeholder="At PrimeConstruct, We're Building A 15-Story Commercial Tower With Modern Office Spaces, Executive Suites, A Rooftop Terrace, And Retail Outlets. Designed For Sustainability And Functionality, It Offers Premium, Flexible Work Environments With Top-Notch Facilities."
            rows={4}
            className="w-full h-[156px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="mb-4">
          {loading ? (
            <div className="text-center py-4">Loading photos...</div>
          ) : (
            <ProgressPhotos
              photos={progressPhotos}
              progress={true}
              selectedPhotos={selectedPhotos}
              handlePhotoSelect={handlePhotoSelect}
              handleRemovePhoto={handleRemovePhoto}
            />
          )}
        </div>

        <div className={`w-full flex justify-start ${!canAccessFeature() ? 'opacity-75' : ''}`}>
          <button
            onClick={handleSubmit}
            className={`w-[179px] mb-2 h-[44px] text-white font-medium text-[20px] rounded-[4px] 
              ${!canAccessFeature()
                ? 'bg-gray-400'
                : 'bg-[#F97316] hover:bg-orange-600'}`}
          >
            {isLoading ? <ButtonLoader /> : <p>Send Progress</p>}
          </button>
        </div>
      </div>


      <UpgradePromptModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        featureName="Send Progress"
        requiredPlan="Premium"
      />
    </div>
  );
};

export default SendProgress;
